import {
  configureStore,
  ThunkAction,
  Action,
  combineReducers,
} from '@reduxjs/toolkit';

import authReducer from './authSlice';
import passwordReducer from './passwordSlice';
import accountReducer from './accountSlice';
import userReducer from './userSlice';
import bankReducer from './bankStore/bankSlice';
import currencyReducer from './currencyStore/currencySlice';
import countryReducer from './countryStore/countrySlice';
import stateReducer from './stateStore/stateSlice';
import addressReducer from './addressStore/addressSlice';
import documentSettingReducer from './documentSettingStore/documentSettingSlice';
import documentTypeReducer from './documentTypeStore/documentTypeSlice';
import invoiceReducer from './invoiceStore/invoiceSlice';
import DocAttachTypeReducer from './docAttachSlice';
import inventoryReducer from './inventoryStore/inventorySlice';
import customerReducer from './customerStore/customerSlice';
import supplierReducer from './supplierStore/supplierSlice';
import ARDashboardReducer from './ARDashboardStore/ARDashboardSlice';
import CashInflowReducer from './CashInflowStore/cashInflowSlice';
import CashOutflowReducer from './CashOutflowStore/cashOutflowSlice';
import APDashboardReducer from './APDashboardStore/APDashboardSlice';
import estimateReducer from './estimateStore/estimateSlice';
import creditNoteReducer from './creditNoteStore/creditNoteSlice';
import debitNoteReducer from './debitNoteStore/debitNoteSlice';
import gstReducer from './gstStore/gstSlice';
import employeeReducer from './employeeStore/employeeSlice';
import reportReducer from './reportStore/reportslice';
import tdsReceivableReducer from "./tdsReceivable/tdsReceivableSlice";
import expenseReducer from './expenseStore/expenseSlice';
import ledgerReducer from './ledgerStore/ledgerSlice';
import gstSettlementReducer from './gstSettlementStore/gstSettlementSlice';
import tdsSettlementReducer from './tdsSettlementStore/tdsSettlementSlice';
import tcsSettlementReducer from './tcsSettlementStore/tcsSettlementSlice';
import statutorySettlementReducer from './statutorySettlementStore/statutorySettlementSlice';
import salaryReducer from './salaryStore/salarySlice';
import costCategoryReducer from './costCategoryStore/costCategorySlice';
import costCentreReducer from './costCentreStore/costCentreSlice';
import coaReducer from './coaStore/coaSlice';
import ledgerAdjustmentReducer from './ledgerAdjustmentStore/ledgerAdjustmentSlice';
import auditLogReducer from './auditLogStore/auditLogSlice';
import locationReducer from './locationStore/locationSlice';
import unitsReducer from './unitsStore/unitsSlice';
import stockReducer from './stockStore/stockSlice';
import grnReducer from './grnStore/grnSlice';
import batchReducer from './batchStore/batchSlice';
import requisitionReducer from './requisitionStore/requisitionSlice';
import stockTransferReducer from './stockTransferStore/stockTransferSlice';
import recipeReducer from './recipeStore/recipeSlice';
import openContainerReducer from './openContainerStore/openContainerSlice';
import barcodePatternReducer from './barcodePatternStore/barcodePatternSlice';
import barcodeReducer from './barcodeStore/barcodeSlice';
import skuVariantReducer from './skuVariantStore/skuVariantSlice';
import inventoryDashboardReducer from './inventoryDashboardStore/inventoryDashboardSlice';
import tagReducer from './tagStore/tagSlice';

const combinedReducer = combineReducers({
  auth: authReducer,
  password: passwordReducer,
  account: accountReducer,
  users: userReducer,
  banks: bankReducer,
  currency: currencyReducer,
  country: countryReducer,
  countryState: stateReducer,
  gstState: gstReducer,
  addresses: addressReducer,
  documentSettings: documentSettingReducer,
  documentType: documentTypeReducer,
  invoices: invoiceReducer,
  docAttach: DocAttachTypeReducer,
  inventories: inventoryReducer,
  customers: customerReducer,
  suppliers: supplierReducer,
  ARDashboard: ARDashboardReducer,
  cashInflow: CashInflowReducer,
  cashOutflow: CashOutflowReducer,
  APDashboard: APDashboardReducer,
  estimates: estimateReducer,
  creditNotes: creditNoteReducer,
  debitNotes: debitNoteReducer,
  employees: employeeReducer,
  reports: reportReducer,
  tdsReceivable: tdsReceivableReducer,
  expense: expenseReducer,
  ledgers: ledgerReducer,
  gstSettlement: gstSettlementReducer,
  tdsSettlement: tdsSettlementReducer,
  tcsSettlement: tcsSettlementReducer,
  statutorySettlement: statutorySettlementReducer,
  salaries: salaryReducer,
  costCategories: costCategoryReducer,
  costCentres: costCentreReducer,
  coa: coaReducer,
  ledgerAdjustments: ledgerAdjustmentReducer,
  auditLog: auditLogReducer,
  locations: locationReducer,
  units: unitsReducer,
  stock: stockReducer,
  grns: grnReducer,
  batches: batchReducer,
  requisitions: requisitionReducer,
  stockTransfers: stockTransferReducer,
  recipes: recipeReducer,
  openContainers: openContainerReducer,
  barcodePatterns: barcodePatternReducer,
  barcode: barcodeReducer,
  skuVariants: skuVariantReducer,
  inventoryDashboard: inventoryDashboardReducer,
  tags: tagReducer,
});

/**
 * Root Reducer 
 */
const rootReducer = (state: any, action: any) => {
  if (action.type === 'auth/logout') {
    localStorage.clear();
    state = {} as RootState;
  }

  return combinedReducer(state, action);
}

/**
 * Create store by combining all the reducers.
 */
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
